/**
 * KAN-724: письмо без привязки целиком и разбор: "Привязать к клиенту" (поиск клиента),
 * "Создать клиента" (имя из отображаемого имени отправителя), "Это не клиент".
 * После привязки письмо уходит в переписку клиента, как обычный ответ.
 */
import { useEffect, useState } from 'react';
import { Link2, Paperclip, UserPlus, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ClientSearchPicker } from '@/components/emails/ClientSearchPicker';
import {
  useAssignUnmatchedEmail,
  useCreateClientFromEmail,
  useDismissUnmatchedEmail,
  useUnmatchedEmail,
} from '@/hooks/useUnmatchedEmails';
import { senderLabel } from '@/lib/emailFormat';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { formatCheckedAt } from '@/lib/telegramProxyDirectory';
import { unmatchedEmailsApi } from '@/services/api/unmatchedEmails';
import type { Client } from '@/types/client';
import type { UnmatchedEmailAssignResult, UnmatchedEmailAttachment, UnmatchedEmailItem } from '@/types/emailMailbox';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { ANY_UUID } from '@/lib/managerAccess';

type Mode = 'view' | 'assign' | 'create';

interface Props {
  email: UnmatchedEmailItem | null;
  onClose: () => void;
  /** Открыть переписку клиента (кнопка в уведомлении о привязке). */
  onAssigned?: (result: UnmatchedEmailAssignResult) => void;
}

function Header({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex gap-2 text-sm">
      <span className="w-16 shrink-0 text-gray-500">{label}</span>
      <span className="break-all">{value || '-'}</span>
    </div>
  );
}

async function download(emailId: string, attachment: UnmatchedEmailAttachment): Promise<void> {
  try {
    const blob = await unmatchedEmailsApi.attachment(emailId, attachment.index);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.filename || 'file';
    link.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(extractErrorMessage(err, 'Не удалось скачать файл'));
  }
}

export function UnmatchedEmailDialog({ email, onClose, onAssigned }: Props): JSX.Element {
  const { data: detail, isLoading, isError } = useUnmatchedEmail(email?.id ?? null);
  const [mode, setMode] = useState<Mode>('view');
  const [picked, setPicked] = useState<Client | null>(null);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const assign = useAssignUnmatchedEmail();
  const createClient = useCreateClientFromEmail();
  const dismiss = useDismissUnmatchedEmail();
  const busy = assign.isPending || createClient.isPending || dismiss.isPending;
  // KAN-811: sorting unmatched mail is closed to the MANAGER role: view only.
  const canSort = useCanCallApi('POST', `/api/v1/emails/unmatched/${email?.id ?? ANY_UUID}/assign/`);

  useEffect(() => {
    setMode('view');
    setPicked(null);
    setName(email?.from_name ?? '');
    setCompany('');
  }, [email?.id, email?.from_name]);

  const done = (result: UnmatchedEmailAssignResult, text: string): void => {
    toast.success(
      text,
      onAssigned ? { action: { label: 'Открыть переписку', onClick: () => onAssigned(result) } } : undefined
    );
    onClose();
  };

  const onAssign = (): void => {
    if (!email || !picked) return;
    assign.mutate(
      { id: email.id, clientId: picked.id },
      {
        onSuccess: (result) => done(result, `Письмо привязано к клиенту ${result.client_name || picked.name || ''}`.trim()),
        onError: (err) => toast.error(extractErrorMessage(err, 'Не удалось привязать письмо')),
      }
    );
  };

  const onCreate = (): void => {
    if (!email) return;
    createClient.mutate(
      { id: email.id, body: { name: name.trim() || undefined, company: company.trim() || undefined } },
      {
        onSuccess: (result) => done(result, `Клиент ${result.client_name} создан, письмо в его переписке`),
        onError: (err) => toast.error(extractErrorMessage(err, 'Не удалось создать клиента')),
      }
    );
  };

  const onDismiss = (): void => {
    if (!email) return;
    dismiss.mutate(email.id, {
      onSuccess: () => {
        toast.success('Письмо отмечено: это не клиент');
        onClose();
      },
      onError: (err) => toast.error(extractErrorMessage(err, 'Не удалось отметить письмо')),
    });
  };

  const shown = detail ?? email;

  return (
    <Dialog open={!!email} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="unmatched-detail">
        {shown && (
          <>
            <DialogHeader>
              <DialogTitle className="pr-6">{shown.subject || '(без темы)'}</DialogTitle>
              <DialogDescription>Письмо не привязано к клиенту</DialogDescription>
            </DialogHeader>
            <div className="space-y-1">
              <Header label="От" value={senderLabel(shown)} />
              <Header label="Кому" value={shown.to} />
              <Header label="Дата" value={formatCheckedAt(shown.created_at)} />
            </div>
            {isLoading && <p className="text-sm text-gray-500">Загрузка письма...</p>}
            {isError && <p className="text-sm text-red-700">Не удалось загрузить письмо</p>}
            {detail && (
              <pre
                className="max-h-[40vh] overflow-auto whitespace-pre-wrap break-words rounded-md border bg-gray-50 p-3 font-sans text-sm"
                data-testid="unmatched-detail-body"
              >
                {detail.body || '(пустое письмо)'}
              </pre>
            )}
            {detail && detail.attachments.length > 0 && (
              <div data-testid="unmatched-detail-attachments">
                <p className="mb-1 text-xs font-medium text-gray-500">Вложения</p>
                <ul className="space-y-1">
                  {detail.attachments.map((a) => (
                    <li key={a.index} className="flex items-center gap-2 text-sm">
                      <Paperclip className="h-3.5 w-3.5 text-gray-500" />
                      {a.downloadable ? (
                        <button
                          type="button"
                          className="text-blue-700 hover:underline"
                          onClick={() => void download(shown.id, a)}
                        >
                          {a.filename || 'файл'}
                        </button>
                      ) : (
                        <span>{a.filename || 'файл'}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {mode === 'assign' && (
              <div className="space-y-2 border-t pt-3" data-testid="unmatched-assign">
                <p className="text-sm font-medium">Привязать к клиенту</p>
                <ClientSearchPicker selectedId={picked?.id ?? null} onSelect={setPicked} />
              </div>
            )}
            {mode === 'create' && (
              <div className="space-y-3 border-t pt-3" data-testid="unmatched-create">
                <div className="space-y-1">
                  <Label htmlFor="unmatched-client-name">Имя</Label>
                  <Input id="unmatched-client-name" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="unmatched-client-company">Компания</Label>
                  <Input
                    id="unmatched-client-company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </div>
                <p className="text-xs text-gray-500">Email клиента: {shown.from}</p>
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-2">
              {mode === 'view' && canSort && (
                <>
                  <Button variant="outline" disabled={busy} onClick={onDismiss} data-testid="unmatched-dismiss">
                    <XCircle className="mr-1 h-4 w-4" />
                    Это не клиент
                  </Button>
                  <Button variant="outline" disabled={busy} onClick={() => setMode('create')} data-testid="unmatched-create-open">
                    <UserPlus className="mr-1 h-4 w-4" />
                    Создать клиента
                  </Button>
                  <Button disabled={busy} onClick={() => setMode('assign')} data-testid="unmatched-assign-open">
                    <Link2 className="mr-1 h-4 w-4" />
                    Привязать к клиенту
                  </Button>
                </>
              )}
              {mode !== 'view' && (
                <Button variant="outline" disabled={busy} onClick={() => setMode('view')}>
                  Назад
                </Button>
              )}
              {mode === 'assign' && (
                <Button disabled={busy || !picked} onClick={onAssign} data-testid="unmatched-assign-submit">
                  Привязать
                </Button>
              )}
              {mode === 'create' && (
                <Button disabled={busy} onClick={onCreate} data-testid="unmatched-create-submit">
                  Создать и привязать
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
